// DBConnectionContext.js - DB 연결 상태 관리 Context
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const CONNECTION_STATUS_STORAGE_KEY = 'onboard_db_connected';
const CONNECTION_INFO_STORAGE_KEY = 'onboard_db_connection_info';
const CONNECTION_FORM_STORAGE_KEY = 'onboard_db_connection_form';

// 상대 경로 사용 (빈 문자열 = 현재 도메인)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const DBConnectionContext = createContext();

const loadStoredState = () => {
  let isConnected = false;
  let serverInfo = null;

  try {
    const storedStatus = localStorage.getItem(CONNECTION_STATUS_STORAGE_KEY);
    isConnected = storedStatus ? JSON.parse(storedStatus) === true : false;
  } catch {
    isConnected = false;
  }

  try {
    const storedInfo = localStorage.getItem(CONNECTION_INFO_STORAGE_KEY);
    if (storedInfo) {
      const parsed = JSON.parse(storedInfo);
      if (parsed?.server && parsed?.database) {
        serverInfo = {
          server: parsed.server,
          database: parsed.database,
          username: parsed.username ?? undefined,
        };
      }
    }
  } catch {
    serverInfo = null;
  }

  if (!serverInfo) {
    try {
      const storedForm = localStorage.getItem(CONNECTION_FORM_STORAGE_KEY);
      if (storedForm) {
        const parsed = JSON.parse(storedForm);
        if (parsed?.server && parsed?.database) {
          serverInfo = {
            server: parsed.server,
            database: parsed.database,
            username: parsed.username ?? undefined,
          };
        }
      }
    } catch {
      serverInfo = null;
    }
  }

  if (!isConnected) {
    serverInfo = null;
  }

  return { isConnected, serverInfo };
};

export const useDBConnection = () => {
  const context = useContext(DBConnectionContext);
  if (!context) {
    throw new Error('useDBConnection must be used within a DBConnectionProvider');
  }
  return context;
};

export const DBConnectionProvider = ({ children }) => {
  const { isConnected: initialConnected, serverInfo: initialServerInfo } = loadStoredState();
  const [isConnected, setIsConnected] = useState(initialConnected);
  const [serverInfo, setServerInfo] = useState(initialServerInfo);

  useEffect(() => {
    // 애플리케이션 초기 구동 시 백엔드 health check로 실제 DB 연결 상태 확인
    const checkBackendDbStatus = async () => {
      try {
        const response = await axios.get(`/health`, {
          timeout: 5000
        });
        
        const healthData = response.data;
        const backendConnected = healthData.database?.connected === true;
        
        if (backendConnected && healthData.database?.database) {
          // 백엔드가 실제로 연결되어 있으면 그 정보를 사용
          const serverDisplay = `${healthData.database.host || 'localhost'}:${healthData.database.port || 3306}`;
          const dbInfo = {
            server: serverDisplay,
            database: healthData.database.database,
            username: healthData.database.user || undefined,
          };
          
          setIsConnected(true);
          setServerInfo(dbInfo);
          
          // localStorage에도 저장
          try {
            localStorage.setItem(CONNECTION_STATUS_STORAGE_KEY, JSON.stringify(true));
            localStorage.setItem(
              CONNECTION_INFO_STORAGE_KEY,
              JSON.stringify({
                server: serverDisplay,
                database: healthData.database.database,
                username: healthData.database.user || '',
              })
            );
          } catch {
            //
          }
        } else {
          // 백엔드가 연결되지 않았으면 localStorage 상태 확인
          const storedState = loadStoredState();
          if (storedState.isConnected && storedState.serverInfo) {
            setIsConnected(true);
            setServerInfo(storedState.serverInfo);
          } else {
            setIsConnected(false);
            setServerInfo(null);
            try {
              localStorage.setItem(CONNECTION_STATUS_STORAGE_KEY, JSON.stringify(false));
            } catch {
              //
            }
          }
        }
      } catch (error) {
        // health check 실패 시 localStorage 상태 사용
        console.warn('백엔드 health check 실패, localStorage 상태 사용:', error.message);
        const storedState = loadStoredState();
        if (storedState.isConnected && storedState.serverInfo) {
          setIsConnected(true);
          setServerInfo(storedState.serverInfo);
        } else {
          setIsConnected(false);
          setServerInfo(null);
          try {
            localStorage.setItem(CONNECTION_STATUS_STORAGE_KEY, JSON.stringify(false));
          } catch {
            //
          }
        }
      }
    };
    
    checkBackendDbStatus();
    
    // 주기적으로 백엔드 상태 확인 (5분마다 - 빈도 줄임)
    const healthCheckInterval = setInterval(checkBackendDbStatus, 300000);
    
    return () => {
      clearInterval(healthCheckInterval);
    };
  }, []);

  const connect = useCallback((server, database, username) => {
    setIsConnected(true);
    const info = {
      server,
      database,
      username: username ?? undefined,
    };
    setServerInfo(info);
    try {
      localStorage.setItem(CONNECTION_STATUS_STORAGE_KEY, JSON.stringify(true));
      localStorage.setItem(
        CONNECTION_INFO_STORAGE_KEY,
        JSON.stringify({
          server,
          database,
          username: username ?? '',
        })
      );
      localStorage.setItem(
        CONNECTION_FORM_STORAGE_KEY,
        JSON.stringify({
          server,
          database,
          authType: username ? 'sql' : 'windows',
          username: username ?? '',
        })
      );
    } catch {
      //
    }
  }, []);

  const disconnect = useCallback(() => {
    setIsConnected(false);
    setServerInfo(null);
    try {
      localStorage.setItem(CONNECTION_STATUS_STORAGE_KEY, JSON.stringify(false));
      localStorage.removeItem(CONNECTION_INFO_STORAGE_KEY);
    } catch {
      //
    }
  }, []);

  useEffect(() => {
    const handleConnectionChanged = (event) => {
      const detail = event.detail || {};
      const connected = detail.connected === true;

      if (connected) {
        const storedForm = loadStoredState().serverInfo;
        const nextInfo = {
          server: detail.server || storedForm?.server || serverInfo?.server || '',
          database: detail.database || storedForm?.database || serverInfo?.database || '',
          username: detail.username ?? storedForm?.username ?? serverInfo?.username ?? undefined,
        };

        if (nextInfo.server && nextInfo.database) {
          setIsConnected(true);
          setServerInfo(nextInfo);
          try {
            localStorage.setItem(CONNECTION_STATUS_STORAGE_KEY, JSON.stringify(true));
            localStorage.setItem(
              CONNECTION_INFO_STORAGE_KEY,
              JSON.stringify({
                server: nextInfo.server,
                database: nextInfo.database,
                username: nextInfo.username ?? '',
              })
            );
          } catch {
            //
          }
        }
      } else {
        setIsConnected(false);
        setServerInfo(null);
        try {
          localStorage.setItem(CONNECTION_STATUS_STORAGE_KEY, JSON.stringify(false));
          localStorage.removeItem(CONNECTION_INFO_STORAGE_KEY);
        } catch {
          //
        }
      }
    };

    window.addEventListener('dbConnectionChanged', handleConnectionChanged);
    return () => window.removeEventListener('dbConnectionChanged', handleConnectionChanged);
  }, [serverInfo]);

  const value = {
    isConnected,
    serverInfo,
    connect,
    disconnect,
  };

  return (
    <DBConnectionContext.Provider value={value}>
      {children}
    </DBConnectionContext.Provider>
  );
};

